import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthAPI, UserAPI } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUsage = useCallback(async () => {
    try {
      const u = await UserAPI.usage();
      setUsage(u.data);
    } catch {
      setUsage(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("fdl_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const me = await AuthAPI.me();
      setUser(me.data);
      await refreshUsage();
    } catch {
      localStorage.removeItem("fdl_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshUsage]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const applyToken = async (data) => {
    localStorage.setItem("fdl_token", data.access_token);
    setUser(data.user);
    await refreshUsage();
  };

  const login = async (email, password) => {
    const r = await AuthAPI.login(email, password);
    await applyToken(r.data);
    return r.data.user;
  };

  const register = async (email, password, name, ref) => {
    const r = await AuthAPI.register(email, password, name, ref);
    await applyToken(r.data);
    return r.data.user;
  };

  const googleAuth = async (session_id) => {
    const r = await AuthAPI.google(session_id);
    await applyToken(r.data);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("fdl_token");
    setUser(null);
    setUsage(null);
  };

  const value = { user, usage, loading, login, register, googleAuth, logout, refreshUsage, setUser };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
